import React from 'react';

const EARLY_PREGNANCY_TABS = ['Uterus', 'Endometrium', 'Cervix', 'Right Ovary', 'Left Ovary', 'Cul-de-sac'];
const LATER_PREGNANCY_TABS = EARLY_PREGNANCY_TABS.filter(tab => tab !== 'Endometrium');

const OVARY_STATUS_OPTIONS = ['Normal', 'Not Visualized', 'Oophorectomy', 'CL/Follicle (<3cm)', 'Polycystic'];

const OVARY_MASS_TYPES = [
  'Cyst - Unilocular',
  'Cyst - Multilocular',
  'Hemorrhagic Cyst',
  'Endometrioma',
  'Dermoid Cyst',
  'Hydrosalpinx',
  'Complex',
  'Solid'
];

const EARLY_PREGNANCY_MASS_TYPES = ['Ectopic pregnancy', 'Corpus luteum'];

function createOvary() {
  return { status: ['Normal'], length: '', width: '', depth: '', other: '', masses: [] };
}

// Initial state for forms that keep these fields together under maternalStructures
export function createMaternalStructures() {
  return {
    uterus: 'Normal',
    priorCSection: false,
    placentaScarDistance: '',
    uterusMasses: [],
    cervixLength: '',
    cervixNotVisualized: false,
    cervixFunneling: false,
    rightOvary: createOvary(),
    leftOvary: createOvary(),
    culDeSac: { fluidVolume: 'No free fluid', other: '' }
  };
}

function OvaryTab({ title, ovary, onChange, massTypes }) {
  const isUnavailable = ovary.status.includes('Not Visualized');

  const handleStatusChange = (option) => {
    let status;
    if (ovary.status.includes(option)) {
      status = ovary.status.filter(s => s !== option);
      if (status.length === 0) status = ['Normal'];
    } else if (option === 'Normal' || option === 'Not Visualized') {
      status = [option];
    } else {
      status = [...ovary.status.filter(s => s !== 'Normal' && s !== 'Not Visualized'), option];
    }
    onChange({ status });
  };

  const addMass = () => {
    onChange({
      masses: [...ovary.masses, { size1: '', size2: '', size3: '', echotexture: massTypes[0], description: '' }]
    });
  };

  const removeMass = (index) => {
    onChange({ masses: ovary.masses.filter((_, i) => i !== index) });
  };

  const handleMassChange = (index, field, value) => {
    onChange({ masses: ovary.masses.map((mass, i) => (i === index ? { ...mass, [field]: value } : mass)) });
  };

  return (
    <div className="tab-content-grid">
      <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>{title}</h3>

      <div className="form-group">
        <label>Status (select all that apply):</label>
        <div className="checkbox-group">
          {OVARY_STATUS_OPTIONS.map(option => {
            const disabled = (isUnavailable && option !== 'Not Visualized') ||
                             (ovary.masses.length > 0 && option === 'Normal');
            return (
              <label key={option} style={{ opacity: disabled ? 0.5 : 1 }}>
                <input
                  type="checkbox"
                  checked={ovary.status.includes(option)}
                  onChange={() => handleStatusChange(option)}
                  disabled={disabled}
                />
                {option}
              </label>
            );
          })}
        </div>
      </div>

      {!isUnavailable && !ovary.status.includes('Oophorectomy') && (
        <div className="size-inputs">
          <label>Size (cm):</label>
          <input
            type="number"
            step="0.1"
            placeholder="Length"
            value={ovary.length}
            onChange={(e) => onChange({ length: e.target.value })}
          />
          <span>×</span>
          <input
            type="number"
            step="0.1"
            placeholder="Width"
            value={ovary.width}
            onChange={(e) => onChange({ width: e.target.value })}
          />
          <span>×</span>
          <input
            type="number"
            step="0.1"
            placeholder="Depth"
            value={ovary.depth}
            onChange={(e) => onChange({ depth: e.target.value })}
          />
          {ovary.length && ovary.width && ovary.depth && (
            <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>
              = {(ovary.length * ovary.width * ovary.depth * 0.52).toFixed(1)} cc
            </span>
          )}
        </div>
      )}

      <div className="form-group">
        <label>Additional Findings:</label>
        <input
          type="text"
          value={ovary.other}
          onChange={(e) => onChange({ other: e.target.value })}
          placeholder="Other observations"
        />
      </div>

      <button type="button" onClick={addMass} className="add-mass-btn">
        + Add Cyst/Mass
      </button>

      {ovary.masses.map((mass, index) => (
        <div key={index} className="mass-section">
          <h4>{title} Cyst/Mass {index + 1}</h4>

          <div className="size-inputs">
            <label>Size (cm):</label>
            <input
              type="number"
              step="0.1"
              placeholder="L"
              value={mass.size1}
              onChange={(e) => handleMassChange(index, 'size1', e.target.value)}
            />
            <span>×</span>
            <input
              type="number"
              step="0.1"
              placeholder="W"
              value={mass.size2}
              onChange={(e) => handleMassChange(index, 'size2', e.target.value)}
            />
            <span>×</span>
            <input
              type="number"
              step="0.1"
              placeholder="H"
              value={mass.size3}
              onChange={(e) => handleMassChange(index, 'size3', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Echotexture:</label>
            <select
              value={mass.echotexture}
              onChange={(e) => handleMassChange(index, 'echotexture', e.target.value)}
            >
              {massTypes.map(type => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Description:</label>
            <textarea
              rows="2"
              value={mass.description}
              onChange={(e) => handleMassChange(index, 'description', e.target.value)}
              placeholder="Additional details about the cyst/mass"
            />
          </div>

          <button type="button" onClick={() => removeMass(index)} className="remove-btn">
            Remove Cyst/Mass
          </button>
        </div>
      ))}
    </div>
  );
}

// The maternal tabs shared by the First Trimester, Second/Third Trimester and BPP worksheets.
// `maternal` has the createMaternalStructures() shape; onChange receives the changed fields only.
// earlyPregnancy adds the Endometrium tab and the first-trimester mass types.
function MaternalStructureTabs({ maternal, onChange, activeTab, onTabChange, earlyPregnancy = false }) {
  const { endometrium, culDeSac } = maternal;
  const sectionTabs = earlyPregnancy ? EARLY_PREGNANCY_TABS : LATER_PREGNANCY_TABS;
  const activeSection = sectionTabs[activeTab];
  const ovaryMassTypes = earlyPregnancy ? [...EARLY_PREGNANCY_MASS_TYPES, ...OVARY_MASS_TYPES] : OVARY_MASS_TYPES;

  const addUterusMass = () => {
    const { uterus } = maternal;
    onChange({
      // A described mass means the uterus isn't "Normal" on the report; free text stays as typed
      uterus: !uterus || uterus === 'Normal' ? 'Fibroids' : uterus,
      uterusMasses: [...maternal.uterusMasses, {
        location: '',
        locationType: 'Intramural',
        size1: '',
        size2: '',
        size3: '',
        echotexture: 'Hypoechoic',
        description: 'Typical fibroid'
      }]
    });
  };

  const removeUterusMass = (index) => {
    const uterusMasses = maternal.uterusMasses.filter((_, i) => i !== index);
    onChange({
      // Last mass removed — undo the automatic "Fibroids" entry
      uterus: uterusMasses.length === 0 && maternal.uterus === 'Fibroids' ? 'Normal' : maternal.uterus,
      uterusMasses
    });
  };

  const handleUterusMassChange = (index, field, value) => {
    onChange({
      uterusMasses: maternal.uterusMasses.map((mass, i) => (i === index ? { ...mass, [field]: value } : mass))
    });
  };

  const updateEndometrium = (fields) => onChange({ endometrium: { ...endometrium, ...fields } });
  const updateCulDeSac = (fields) => onChange({ culDeSac: { ...culDeSac, ...fields } });

  return (
    <div className="form-section">
      <div className="worksheet-tabs">
        {sectionTabs.map((tabLabel, tabIndex) => (
          <button
            key={tabLabel}
            type="button"
            onClick={() => onTabChange(tabIndex)}
            className={`worksheet-tab-btn${activeTab === tabIndex ? ' active' : ''}`}
          >
            {tabLabel}
          </button>
        ))}
      </div>

      <div className="worksheet-tab-content">
        {/* Uterus Tab */}
        {activeSection === 'Uterus' && (
          <div className="tab-content-grid">
            <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Uterus</h3>

            <div className="form-group">
              <label>Uterus:</label>
              <input
                type="text"
                value={maternal.uterus}
                onChange={(e) => onChange({ uterus: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', margin: 0, whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={maternal.priorCSection}
                    onChange={(e) => onChange({ priorCSection: e.target.checked })}
                    style={{ marginRight: '8px' }}
                  />
                  Prior C-Section
                </label>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Distance from Placenta (cm):</label>
                  <input
                    type="text"
                    value={maternal.placentaScarDistance || ''}
                    onChange={(e) => onChange({ placentaScarDistance: e.target.value })}
                    disabled={!maternal.priorCSection}
                    placeholder="Enter distance"
                    style={{
                      width: '150px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      backgroundColor: !maternal.priorCSection ? 'var(--color-disabled-bg)' : 'var(--color-input-bg)',
                      cursor: !maternal.priorCSection ? 'not-allowed' : 'text',
                      color: !maternal.priorCSection ? 'var(--color-disabled-text)' : 'var(--color-text)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>
            </div>

            <button type="button" onClick={addUterusMass} className="add-mass-btn">
              + Add Fibroid/Mass
            </button>

            {maternal.uterusMasses.map((mass, index) => (
              <div key={index} className="mass-section">
                <h4>Uterine Mass/Fibroid {index + 1}</h4>

                <div className="form-group">
                  <label>Location:</label>
                  <input
                    type="text"
                    placeholder="e.g., Anterior wall, Fundal"
                    value={mass.location}
                    onChange={(e) => handleUterusMassChange(index, 'location', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Location Type:</label>
                  <select
                    value={mass.locationType}
                    onChange={(e) => handleUterusMassChange(index, 'locationType', e.target.value)}
                  >
                    <option value="Intramural">Intramural</option>
                    <option value="Submucosal">Submucosal</option>
                    <option value="Subserosal">Subserosal</option>
                    <option value="Pedunculated">Pedunculated</option>
                  </select>
                </div>

                <div className="size-inputs">
                  <label>Size (cm):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="L"
                    value={mass.size1}
                    onChange={(e) => handleUterusMassChange(index, 'size1', e.target.value)}
                  />
                  <span>×</span>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="W"
                    value={mass.size2}
                    onChange={(e) => handleUterusMassChange(index, 'size2', e.target.value)}
                  />
                  <span>×</span>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="H"
                    value={mass.size3}
                    onChange={(e) => handleUterusMassChange(index, 'size3', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Echotexture:</label>
                  <select
                    value={mass.echotexture}
                    onChange={(e) => handleUterusMassChange(index, 'echotexture', e.target.value)}
                  >
                    <option value="Hypoechoic">Hypoechoic</option>
                    <option value="Hyperechoic">Hyperechoic</option>
                    <option value="Complex">Complex</option>
                    <option value="Calcified">Calcified</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Description:</label>
                  <select
                    value={mass.description}
                    onChange={(e) => handleUterusMassChange(index, 'description', e.target.value)}
                  >
                    <option value="Typical fibroid">Typical fibroid</option>
                    <option value="Calcified fibroid">Calcified fibroid</option>
                    <option value="Complex mass">Complex mass</option>
                  </select>
                </div>

                <button type="button" onClick={() => removeUterusMass(index)} className="remove-btn">
                  Remove Mass
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Endometrium Tab */}
        {activeSection === 'Endometrium' && (
          <div className="tab-content-grid">
            <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Endometrium</h3>

            <div className="form-group">
              <label>Endometrial Thickness (mm):</label>
              <input
                type="text"
                value={endometrium.thickness}
                onChange={(e) => updateEndometrium({ thickness: e.target.value })}
                placeholder="Enter thickness in mm"
              />
            </div>

            <div className="form-group">
              <label style={{ visibility: 'hidden' }}>Retained Products</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <input
                  type="checkbox"
                  checked={endometrium.retainedProducts}
                  onChange={(e) => updateEndometrium({ retainedProducts: e.target.checked })}
                />
                Retained Products
              </label>
            </div>

            <div className="form-group" style={{ width: '150%' }}>
              <label>Additional Findings:</label>
              <input
                type="text"
                value={endometrium.otherFindings}
                onChange={(e) => updateEndometrium({ otherFindings: e.target.value })}
                placeholder="Other observations"
              />
            </div>
          </div>
        )}

        {/* Cervix Tab */}
        {activeSection === 'Cervix' && (
          <div className="tab-content-grid">
            <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Cervix</h3>

            <div className="form-row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px' }}>
              <div className="form-group" style={{ flex: '0 1 220px' }}>
                <label>Cervix Length (cm):</label>
                <input
                  type="text"
                  value={maternal.cervixLength}
                  onChange={(e) => onChange({ cervixLength: e.target.value })}
                  disabled={maternal.cervixNotVisualized}
                  placeholder={maternal.cervixNotVisualized ? 'Not well visualized' : 'Enter cervical length'}
                />
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginTop: '27px', minWidth: 'fit-content' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={!!maternal.cervixNotVisualized}
                    onChange={(e) => onChange({
                      cervixNotVisualized: e.target.checked,
                      // Length and funneling can't be assessed without a view of the cervix
                      cervixLength: e.target.checked ? '' : maternal.cervixLength,
                      cervixFunneling: e.target.checked ? false : maternal.cervixFunneling
                    })}
                  />
                  Cervix not well visualized
                </label>
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginTop: '27px', minWidth: 'fit-content' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={!!maternal.cervixFunneling}
                    onChange={(e) => onChange({ cervixFunneling: e.target.checked })}
                    disabled={maternal.cervixNotVisualized}
                  />
                  Funneling
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Right Ovary Tab */}
        {activeSection === 'Right Ovary' && (
          <OvaryTab
            title="Right Ovary"
            ovary={maternal.rightOvary}
            onChange={(fields) => onChange({ rightOvary: { ...maternal.rightOvary, ...fields } })}
            massTypes={ovaryMassTypes}
          />
        )}

        {/* Left Ovary Tab */}
        {activeSection === 'Left Ovary' && (
          <OvaryTab
            title="Left Ovary"
            ovary={maternal.leftOvary}
            onChange={(fields) => onChange({ leftOvary: { ...maternal.leftOvary, ...fields } })}
            massTypes={ovaryMassTypes}
          />
        )}

        {/* Cul-de-sac Tab */}
        {activeSection === 'Cul-de-sac' && (
          <div className="tab-content-grid">
            <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Cul-de-sac</h3>

            <div className="form-group">
              <label>Free Fluid:</label>
              <select
                value={culDeSac.fluidVolume}
                onChange={(e) => updateCulDeSac({ fluidVolume: e.target.value })}
              >
                <option value="No free fluid">No free fluid</option>
                <option value="Small volume free fluid">Small volume free fluid</option>
                <option value="Moderate volume free fluid">Moderate volume free fluid</option>
                <option value="Large volume free fluid">Large volume free fluid</option>
              </select>
            </div>

            <div className="form-group">
              <label>Additional Findings:</label>
              <input
                type="text"
                value={culDeSac.other}
                onChange={(e) => updateCulDeSac({ other: e.target.value })}
                placeholder="Other observations"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MaternalStructureTabs;
